import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, Loader2, RefreshCw, Save, CheckCircle2, Clock } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export const AdminPaymentsPanel = () => {
  const [wallet, setWallet] = useState("");
  const [savedWallet, setSavedWallet] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);

  const load = async () => {
    try {
      const [w, o] = await Promise.all([api.get("/admin/wallet"), api.get("/admin/payments")]);
      setWallet(w.data.wallet_address || "");
      setSavedWallet(w.data.wallet_address || "");
      setOrders(o.data);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveWallet = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/wallet", { wallet_address: wallet.trim() });
      setSavedWallet(data.wallet_address);
      toast.success("Cüzdan adresi kaydedildi");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const scan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/admin/payments/scan", { blocks: 10000 });
      if (data.error) toast.error(data.error);
      else toast.success(`Tarama tamam · ${data.confirmed?.length || 0} ödeme onaylandı · ${data.scanned} transfer tarandı`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Tarama başarısız");
    } finally {
      setScanning(false);
    }
  };

  const pending = orders.filter((o) => o.status === "pending").length;

  return (
    <div data-testid="admin-payments-panel" className="rounded-2xl border" style={{ background: "var(--bg-card)", borderColor: "var(--border-subtle)" }}>
      <div className="px-5 sm:px-6 py-4 border-b flex items-center gap-2 flex-wrap" style={{ borderColor: "var(--border-subtle)" }}>
        <Wallet className="w-4 h-4 text-amber-400" />
        <h3 className="text-sm font-bold">Ödemeler · USDT (BEP20)</h3>
        {pending > 0 && <span className="text-[11px] font-semibold px-2 py-1 rounded-full" style={{ background: "rgba(245,158,11,0.15)", color: "#F59E0B" }}>{pending} bekliyor</span>}
        <button data-testid="scan-payments-button" onClick={scan} disabled={scanning} className="ml-auto inline-flex items-center gap-2 text-[12px] font-semibold px-3 py-1.5 rounded-lg border transition-all hover:brightness-110 disabled:opacity-40" style={{ borderColor: "var(--border-subtle)" }}>
          {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} BSC Tara
        </button>
      </div>

      <div className="px-5 sm:px-6 py-4 space-y-4">
        <div>
          <label className="block text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>Alıcı BEP20 Cüzdan Adresi (BSC)</label>
          <div className="flex gap-2">
            <input data-testid="admin-wallet-input" type="text" value={wallet} onChange={(e) => setWallet(e.target.value)} placeholder="0x..."
              className="flex-1 rounded-lg px-3 py-2 text-sm font-mono-term outline-none border focus:border-blue-500"
              style={{ background: "var(--bg-main)", borderColor: "var(--border-subtle)", color: "var(--text-primary)" }} />
            <button data-testid="save-wallet-button" onClick={saveWallet} disabled={saving || wallet.trim() === savedWallet}
              className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-xl text-white transition-all hover:brightness-110 disabled:opacity-50" style={{ background: "#3B82F6" }}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Kaydet
            </button>
          </div>
          <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>Kullanıcılar ödemeyi bu adrese USDT (BEP20) olarak gönderecek. Benzersiz tutar eşleşince kredi otomatik yüklenir.</p>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /></div>
          ) : orders.length === 0 ? (
            <div className="text-center py-6 text-sm" style={{ color: "var(--text-muted)" }}>Henüz ödeme talebi yok</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                  <th className="text-left px-2 py-2">Kullanıcı</th>
                  <th className="text-left px-2 py-2">Paket</th>
                  <th className="text-left px-2 py-2">Beklenen</th>
                  <th className="text-left px-2 py-2">Durum</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} data-testid={`payment-row-${o.id}`} className="border-t" style={{ borderColor: "var(--border-subtle)" }}>
                    <td className="px-2 py-2 font-semibold">{o.username}</td>
                    <td className="px-2 py-2">{o.base_usd}$ · {o.credits}</td>
                    <td className="px-2 py-2 font-mono-term">{o.expected_amount}</td>
                    <td className="px-2 py-2">
                      {o.status === "confirmed"
                        ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: "#10B981" }}><CheckCircle2 className="w-3 h-3" /> Onaylandı</span>
                        : o.status === "pending"
                          ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: "#F59E0B" }}><Clock className="w-3 h-3" /> Bekliyor</span>
                          : <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>{o.status}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};
